<template>
  <div class="OrgMemberProjectAccess pa-4">
    <div class="OrgMemberProjectAccess__title mb-3">{{ projectTerms.accessTitle }}</div>

    <div v-if="loading" class="OrgMemberProjectAccess__status">
      {{ projectTerms.loadingPlural }}
    </div>
    <div v-else-if="error" class="OrgMemberProjectAccess__error pa-3 mb-2">
      {{ error }}
    </div>
    <div
      v-else-if="!memberships.length"
      class="OrgMemberProjectAccess__status"
    >
      {{ projectTerms.noInOrganisation }}
    </div>
    <div v-else class="OrgMemberProjectAccess__list">
      <div
        v-for="membership in memberships"
        :key="membership.project_id"
        class="OrgMemberProjectAccess__group"
      >
        <div class="OrgMemberProjectAccess__row">
          <span class="OrgMemberProjectAccess__project-name">
            {{ membership.project_name }}
          </span>
          <v-switch
            :input-value="switchValue(membership)"
            :disabled="isRowSaving(membership.project_id)"
            dense
            hide-details
            class="OrgMemberProjectAccess__toggle"
            @change="onToggle(membership, $event)"
          />
          <v-select
            v-if="showRoleSelect(membership)"
            :value="roleOptionFor(membership)"
            :items="roleOptions"
            item-text="label"
            item-value="value"
            return-object
            dense
            outlined
            hide-details
            :disabled="isRowSaving(membership.project_id)"
            class="OrgMemberProjectAccess__role-select"
            :menu-props="{
              contentClass: 'AccessTable__table_role_select',
              bottom: true,
              offsetY: true,
            }"
            @change="onRoleChange(membership, $event)"
          />
          <span v-else class="OrgMemberProjectAccess__role-placeholder" />
        </div>
        <p
          v-if="showsAllSitesHint(membership)"
          class="OrgMemberProjectAccess__all-sites-hint mb-0"
        >
          {{ projectTerms.sitesInContainer }}
        </p>
        <OrgMemberSiteAccessList
          v-if="showsSiteAccessList(membership)"
          :organisation-user-id="organisationUserId"
          :user-id="userId"
          :project-id="membership.project_id"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  PROJECT_ROLE,
  getOrgMemberProjectRoleOptions,
  shouldManageSiteAccessForProjectMembership,
} from '@/utils/membershipPermissions'
import OrgMemberSiteAccessList from '@/components/TheOrganisation/OrgMemberSiteAccessList'

export default {
  name: 'OrgMemberProjectAccess',
  components: { OrgMemberSiteAccessList },
  props: {
    organisationUserId: {
      type: [Number, String],
      required: true,
    },
    userId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      error: '',
      memberships: [],
      savingProjectId: null,
      enablingProjectId: null,
    }
  },
  computed: {
    projectTerms() {
      return this.$store.getters['organisations/projectTerms']
    },
    roleOptions() {
      return getOrgMemberProjectRoleOptions(
        this.$store.state.organisations.current
      )
    },
  },
  created() {
    this.loadMemberships()
  },
  methods: {
    hasAccess(membership) {
      return membership.project_user_id != null
    },
    isEnablingProject(projectId) {
      return this.enablingProjectId === projectId
    },
    switchValue(membership) {
      return (
        this.hasAccess(membership) ||
        this.isEnablingProject(membership.project_id)
      )
    },
    showRoleSelect(membership) {
      return (
        this.hasAccess(membership) ||
        this.isEnablingProject(membership.project_id)
      )
    },
    showsAllSitesHint(membership) {
      return (
        this.hasAccess(membership) &&
        membership.role === PROJECT_ROLE.MANAGER
      )
    },
    showsSiteAccessList(membership) {
      return shouldManageSiteAccessForProjectMembership(membership)
    },
    extractProjectUser(res) {
      const body = res?.body
      if (!body) return null
      if (body.project_user) return body.project_user
      if (body.id && body.project_id) return body
      return null
    },
    applyProjectUser(membership, projectUser, fallbackRole) {
      if (!projectUser?.id) return false

      this.$set(membership, 'project_user_id', projectUser.id)
      this.$set(
        membership,
        'role',
        projectUser.role || fallbackRole || PROJECT_ROLE.MEMBER
      )
      return true
    },
    async syncMembershipRow(projectId) {
      const latest = await this.$store.dispatch(
        'organisation_user/loadProjectMemberships',
        this.organisationUserId
      )
      const updated = latest.find((row) => row.project_id === projectId)
      const index = this.memberships.findIndex(
        (row) => row.project_id === projectId
      )

      if (updated && index !== -1) {
        this.$set(this.memberships, index, updated)
      }
    },
    isRowSaving(projectId) {
      return this.savingProjectId === projectId
    },
    roleOptionFor(membership) {
      const role = membership.role || PROJECT_ROLE.MEMBER
      return (
        this.roleOptions.find((option) => option.value === role) ||
        this.roleOptions[1]
      )
    },
    async loadMemberships() {
      this.loading = true
      this.error = ''
      try {
        this.memberships = await this.$store.dispatch(
          'organisation_user/loadProjectMemberships',
          this.organisationUserId
        )
      } catch (error) {
        this.error =
          error?.body?.message ||
          error?.message ||
          this.projectTerms.failedAccessLoad
      } finally {
        this.loading = false
      }
    },
    async onToggle(membership, enabled) {
      if (this.isRowSaving(membership.project_id)) return

      this.savingProjectId = membership.project_id
      this.error = ''

      if (enabled) {
        this.enablingProjectId = membership.project_id
        if (!membership.role) {
          this.$set(membership, 'role', PROJECT_ROLE.MEMBER)
        }
      } else {
        this.enablingProjectId = null
      }

      try {
        if (enabled) {
          const role = membership.role || PROJECT_ROLE.MEMBER
          const res = await this.$store.dispatch('project_user/addUser', {
            user_id: this.userId,
            project_id: membership.project_id,
            role,
          })

          const projectUser = this.extractProjectUser(res)
          if (!this.applyProjectUser(membership, projectUser, role)) {
            await this.syncMembershipRow(membership.project_id)
          }
        } else if (membership.project_user_id) {
          await this.$store.dispatch(
            'project_user/removeMembership',
            membership.project_user_id
          )
          await this.revokeSiteMembershipsForProject(membership.project_id)
          this.$set(membership, 'project_user_id', null)
          this.$set(membership, 'role', null)
        }
      } catch (error) {
        this.error =
          error?.body?.message ||
          error?.message ||
          `Failed to update access for ${membership.project_name}.`
        await this.syncMembershipRow(membership.project_id)
      } finally {
        this.savingProjectId = null
        this.enablingProjectId = null
      }
    },
    async revokeSiteMembershipsForProject(projectId) {
      const siteMemberships = await this.$store.dispatch(
        'organisation_user/loadSiteMemberships',
        {
          organisationUserId: this.organisationUserId,
          projectId,
          userId: this.userId,
        }
      )

      await Promise.all(
        siteMemberships
          .filter((membership) => membership.site_user_id != null)
          .map((membership) =>
            this.$store.dispatch(
              'site_user/removeMembership',
              membership.site_user_id
            )
          )
      )
    },
    async onRoleChange(membership, roleOption) {
      if (!membership.project_user_id || this.isRowSaving(membership.project_id)) {
        return
      }

      const previousRole = membership.role
      this.$set(membership, 'role', roleOption.value)
      this.savingProjectId = membership.project_id
      this.error = ''

      try {
        const res = await this.$store.dispatch('project_user/changeRole', {
          id: membership.project_user_id,
          role: roleOption.value,
        })
        const projectUser = this.extractProjectUser(res)
        if (projectUser?.role) {
          this.$set(membership, 'role', projectUser.role)
        }
      } catch (error) {
        this.$set(membership, 'role', previousRole)
        this.error =
          error?.body?.message ||
          error?.message ||
          `Failed to update role for ${membership.project_name}.`
      } finally {
        this.savingProjectId = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.OrgMemberProjectAccess {
  background: rgba(0, 0, 0, 0.15);
  border-top: 1px solid #2e2c42;

  &__title {
    color: $N2;
    font-size: 13px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__status {
    color: $N3;
    font-size: 13px;
  }

  &__error {
    background: rgba(233, 74, 83, 0.15);
    border: 1px solid rgba(233, 74, 83, 0.35);
    border-radius: 8px;
    color: #ffb4b9;
    font-size: 13px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__group {
    padding: 8px 0;
    border-bottom: 1px solid rgba(46, 44, 66, 0.8);

    &:last-child {
      border-bottom: none;
    }
  }

  &__row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 72px 180px;
    gap: 12px;
    align-items: center;
    padding: 0;
  }

  &__all-sites-hint {
    color: $N3;
    font-size: 12px;
    margin-top: 4px;
    padding-left: 0;
  }

  &__project-name {
    color: $white;
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__toggle {
    justify-self: center;
    margin-top: 0;
  }

  &__role-select {
    max-width: 180px;
  }

  &__role-placeholder {
    display: block;
    min-height: 40px;
  }
}
</style>
